import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useChatInputState, useTripsState } from '#states/hooks.js';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '#ui/dialog.jsx';

import { AppImage } from './AppImage.js';
import { AskElaraButton } from './AskElaraButton.js';
import { Button } from './button.js';
import { MaterialIcon } from './MaterialIcon.js';

export interface ImageAttachment {
  id: string;
  name?: string;
  attribution?: string;
  source?: string;
  type: string;
  mimeType?: string;
  size?: number;
  url?: string;
}

export interface ImageGalleryDialogProps {
  images: ImageAttachment[];
  showCaptions?: boolean;
  /** Show "Ask Elara" (attach the photo to the trip chat). Off on the public shared itinerary, which has no chat. */
  canAskElara?: boolean;
}

export interface ImageGalleryDialogRef {
  openImage: (image: ImageAttachment) => void;
}

export const ImageGalleryDialog = forwardRef<ImageGalleryDialogRef | undefined, ImageGalleryDialogProps>(
  ({ images, showCaptions = false, canAskElara = true }, ref) => {
    const [selectedImage, setSelectedImage] = useState<ImageAttachment | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(false);
    const chatInput = useChatInputState();
    const tripsState = useTripsState();

    useImperativeHandle(ref, () => ({
      openImage: (image: ImageAttachment) => {
        setSelectedImage(image);
        setIsOpen(true);
      },
    }));
    /**
     * Ids whose url did not load. Supplier photo CDNs rot, and a dead image is worse here than in the
     * grid that opened this: there it was one tile among several, here it is the whole screen, and
     * paging onto it looks like the viewer itself has broken. The grid can only pre-filter the photos
     * it actually rendered — anything past its `maxImages` has never been requested — so the lightbox
     * has to answer for the rest itself.
     */
    const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(() => new Set());
    const liveImages = images.filter((img) => !failedIds.has(img.id));

    /** Drops the image on screen and moves to the next survivor, or closes if it was the last. */
    const dropSelectedImage = useCallback(() => {
      if (!selectedImage) return;
      const failedId = selectedImage.id;
      const remaining = images.filter((img) => img.id !== failedId && !failedIds.has(img.id));
      setFailedIds((current) => new Set(current).add(failedId));
      // Prefer the next image in the original order, so paging keeps its direction; fall back to the
      // one before it when the failure was last.
      const failedIndex = images.findIndex((img) => img.id === failedId);
      const next =
        remaining.find((img) => images.indexOf(img) > failedIndex) ??
        remaining.toReversed().find((img) => images.indexOf(img) < failedIndex);
      if (next) {
        setSelectedImage(next);
      } else {
        setIsOpen(false);
        setSelectedImage(undefined);
      }
    }, [selectedImage, images, failedIds]);

    const getCurrentImageIndex = useCallback(() => {
      if (!selectedImage) return -1;
      return liveImages.findIndex((img) => img.id === selectedImage.id);
    }, [selectedImage, liveImages]);

    const goToPreviousImage = useCallback(() => {
      const currentIndex = getCurrentImageIndex();
      if (currentIndex > 0) {
        const prevImage = liveImages[currentIndex - 1];
        if (prevImage) {
          setSelectedImage(prevImage);
        }
      }
    }, [getCurrentImageIndex, liveImages]);

    const goToNextImage = useCallback(() => {
      const currentIndex = getCurrentImageIndex();
      if (currentIndex < liveImages.length - 1) {
        const nextImage = liveImages[currentIndex + 1];
        if (nextImage) {
          setSelectedImage(nextImage);
        }
      }
    }, [getCurrentImageIndex, liveImages]);

    // Keyboard navigation
    useEffect(() => {
      if (!selectedImage) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case 'ArrowLeft': {
            e.preventDefault();
            goToPreviousImage();

            break;
          }
          case 'ArrowRight': {
            e.preventDefault();
            goToNextImage();

            break;
          }
          case 'Escape': {
            e.preventDefault();
            setIsOpen(false);
            setTimeout(() => {
              setSelectedImage(undefined);
            }, 300);

            break;
          }
          // No default
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, images, goToNextImage, goToPreviousImage]);

    const handleAskClick = useCallback(() => {
      if (!selectedImage) return;

      // Save reference to selected image before closing dialog
      const imageToAttach = selectedImage;

      // Close the dialog first
      setIsOpen(false);

      // Clear selected image after animation delay (matches Escape key handler)
      setTimeout(() => {
        setSelectedImage(undefined);
      }, 300);

      // Add image as URL attachment (simpler and avoids CORS issues)
      if (!imageToAttach.url) {
        console.warn('[ImageGalleryDialog] Cannot add image: missing URL');
        return;
      }

      chatInput.addUrlAttachment(
        imageToAttach.url,
        imageToAttach.mimeType || 'image/jpeg',
        imageToAttach.name || imageToAttach.attribution || 'Image',
        imageToAttach.size,
      );

      // Switch to chat panel
      tripsState.switchToPanel('chat');

      // Focus the chat input textarea after dialog close animation completes
      setTimeout(() => {
        chatInput.focusInput();
      }, 300);
    }, [selectedImage, chatInput, tripsState]);

    if (!selectedImage) return undefined;

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsOpen(false);
            setTimeout(() => {
              setSelectedImage(undefined);
            }, 300); // Match the animation duration
          }
        }}
      >
        <DialogContent
          className={cn(
            'flex flex-col gap-y-0.5 w-full h-full max-w-full sm:max-w-full max-h-full p-0 border-none overflow-hidden bg-transparent shadow-none focus:outline-none',
            'duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-8 data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-8 data-[state=closed]:fade-out-0',
            '[&>button]:text-white [&>button]:hover:text-white [&>button]:data-[state=open]:text-white',
          )}
          onPointerDownOutside={(e) => {
            // Allow clicks on overlay/outside to close the dialog
            setIsOpen(false);
            setTimeout(() => {
              setSelectedImage(undefined);
            }, 300);
          }}
          onClick={(e) => {
            // Close dialog when clicking on empty areas (not on image or controls)
            if (e.target === e.currentTarget) {
              setIsOpen(false);
              setTimeout(() => {
                setSelectedImage(undefined);
              }, 300);
            }
          }}
        >
          <DialogTitle className="sr-only">Image gallery</DialogTitle>
          <DialogDescription className="sr-only">{liveImages.length} images</DialogDescription>
          <div
            className="relative flex-1 flex items-center justify-center flex-shrink-1 overflow-hidden p-4 pt-12 md:px-8 md:pb-8"
            onClick={(e) => {
              // Close dialog when clicking on padding areas (not on image)
              if (e.target === e.currentTarget) {
                setIsOpen(false);
                setTimeout(() => {
                  setSelectedImage(undefined);
                }, 300);
              }
            }}
          >
            {selectedImage.url && (
              <AppImage
                key={selectedImage.url}
                src={selectedImage.url}
                alt={selectedImage.name ?? ''}
                onAllFailed={dropSelectedImage}
                loading="eager"
                className="max-w-full max-h-full w-auto h-auto object-contain shadow-md rounded-md"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const centerX = rect.width / 2;

                  if (clickX < centerX) {
                    goToPreviousImage();
                  } else {
                    goToNextImage();
                  }
                }}
              />
            )}
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full flex-shrink-0 p-4 md:p-8">
            {/* Caption */}
            <div className="flex flex-col gap-1 w-full md:w-auto">
              {showCaptions && (
                <>
                  <div className="text-white">{selectedImage.name}</div>
                  {selectedImage.attribution && (
                    <div className="text-white/70 text-sm">
                      {selectedImage.attribution} {selectedImage.source ? `via ${selectedImage.source}` : ''}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-4 w-full md:w-auto">
              {canAskElara && <AskElaraButton className="bg-transparent text-white" onClick={handleAskClick} />}

              {liveImages.length > 1 && (
                <div className="flex items-center gap-2">
                  {/* Previous button */}
                  <Button
                    variant="icon"
                    size="icon"
                    className="bg-black/50 hover:bg-black/70 text-white hover:text-white"
                    onClick={goToPreviousImage}
                    disabled={getCurrentImageIndex() === 0}
                  >
                    <MaterialIcon icon="chevron_left" />
                  </Button>

                  {/* Next button */}
                  <Button
                    variant="icon"
                    size="icon"
                    className="bg-black/50 hover:bg-black/70 text-white hover:text-white"
                    onClick={goToNextImage}
                    disabled={getCurrentImageIndex() === liveImages.length - 1}
                  >
                    <MaterialIcon icon="chevron_right" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);
